<!--
SDEV-FILE
Copyright (c) 2026 SDEV. All rights reserved.
Workspace: clientside
File: DiscoveryView.vue
Created: 2026-03-13T17:44:59.658Z
-->

<template>

    <DiscoveryCoreTemplate :rows="runRows" :loading="loading" :createModel="createModel" :create="startDiscovery"
        :update="update">

        <!-- DISCOVERY RUNS -->

        <template #sources="{ rows, loading, select }">

            <div v-if="loading">Loading discovery runs...</div>

            <div v-else-if="!runRows.length">
                No discovery runs found.
            </div>

            <div v-else>
                <Table :cols="columns" :rows="runRows" @select="select" />
            </div>

        </template>


        <!-- START DISCOVERY -->

        <template #discover>

            <div v-if="!thisRunKey">

                <p>Start a new discovery run</p>

                <button class="sdev-button sdev-bg-surface-elevated" @click="startDiscovery">
                    Start Discovery
                </button>

            </div>

            <div v-else>

                <WizardStep :step="step" :data="sourceData" :sources="sources" :context="discoveryContext"
                    :preview="schemaPreview" :previewLoading="isPreviewLoading" :totalStepsInFlow="totalStepsInFlow"
                    :actualStepInFlow="actualStepInFlow" @next="handleNext" @scan="startScan"
                    @generateBlueprint="generateBlueprint" @saveBlueprint="saveBlueprint" />

            </div>

        </template>


        <!-- SCHEMA INSPECT -->

        <template #inspect>

            <div v-if="!schemaPreview">
                Run discovery to inspect schema.
            </div>

            <div v-else>

                <h3>Identities</h3>

                <ul>
                    <li v-for="i in schemaPreview.identities" :key="i.key">
                        {{ i.key }} — {{ i.type }}
                    </li>
                </ul>

                <h3>Relations</h3>

                <ul>
                    <li v-for="r in schemaPreview.relations" :key="r.parent + r.child">
                        {{ r.parent }} → {{ r.child }} ({{ r.type }})
                    </li>
                </ul>

            </div>

        </template>


        <!-- HISTORY -->

        <template #blueprints>

            <div>
                Discovery run history (future feature)
            </div>

        </template>

    </DiscoveryCoreTemplate>

</template>

<script setup>

import { ref, onMounted } from "vue"

import {

    discoveryRuns,
    discoveryStart,
    discoveryStep,
    discoveryScan,
    discoveryBlueprint

} from "@/domain/discovery/api.discovery"
import DiscoveryCoreTemplate from "../DiscoveryCoreTemplate.vue"

const runRows = ref([])
const columns = ref([])
const loading = ref(false)

const sources = ref([])

const thisRunKey = ref(null)

const step = ref(null)
const sourceData = ref(null)

const schemaPreview = ref(null)
const blueprint = ref(null)

const isPreviewLoading = ref(false)

const totalStepsInFlow = ref(0)
const actualStepInFlow = ref(0)

const discoveryContext = ref({})

const createModel = ref({})

/*
Load existing discovery runs
*/

async function loadRuns() {

    loading.value = true

    const res = await discoveryRuns()

    columns.value = res.data.columns.map(c => ({
        key: c,
        label: c
    }))

    runRows.value = res.data.runs

    loading.value = false

}


/*
Start new discovery run
*/

async function startDiscovery() {

    const res = await discoveryStart()

    thisRunKey.value = res.data.run_key
    step.value = res.data.step

    sources.value = res.data.sources

}


/*
Wizard step handler
*/

async function handleNext(formData) {

    const res = await discoveryStep({

        run_id: thisRunKey.value,
        step: step.value,
        data: formData

    })

    discoveryContext.value = {
        ...discoveryContext.value,
        ...formData
    }

    step.value = res.data.next_step

    actualStepInFlow.value++

}


/*
Scan endpoint
*/

async function startScan(data) {

    isPreviewLoading.value = true

    const res = await discoveryScan({

        run_id: thisRunKey.value,
        endpoint: data.endpoint

    })

    schemaPreview.value = res.data.schema

    isPreviewLoading.value = false

}


/*
Generate blueprint
*/

async function generateBlueprint() {

    const res = await discoveryBlueprint({

        run_id: thisRunKey.value,
        dry_run: true

    })

    blueprint.value = res.data.blueprint

}


/*
Save blueprint
*/

async function saveBlueprint() {

    await discoveryBlueprint({

        run_id: thisRunKey.value,
        dry_run: false

    })

    blueprint.value = null
    thisRunKey.value = null

    loadRuns()

}

onMounted(async () => {

    await loadRuns()

})

</script>